import {
  clearTimetableSelectionFromSession,
  resolveTimetableTenant,
} from 'app/pages/school/Schedule/components/timetable/commons/utils'
import {hasPermission} from 'common/HelperFunctions'
import {DEFAULT_TENANT_VALUE, TENANT_KEY} from 'common/constants'
import {calculateErrorMessage} from 'common/helpers'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useLocation, useNavigate} from 'react-router'

import {getTheme} from '_metronic/partials'

import PromptModal from '../../components/modal/PromptModal'
import {createSmartNavigator} from '../utils/SmartRouteNavigator'
import {performFullWalkthroughCleanup} from '../utils/cleanupHelpers'
import * as Persistence from '../utils/persistence'
import {createProviderDomHelpers} from '../utils/providerDomHelpers'
import {inferTooltipMode, parseApiResponse} from '../utils/providerParse'
import * as StepActions from '../utils/stepActions'
import {
  cleanupAllWalkthroughAttributes,
  findAndTagElement,
  scrollToElement,
} from '../utils/walkthroughDom'
import {DOMCache, buildDomCache, documentReadyHelper} from '../utils/walkthroughDomCache'
import {
  getQuickFindOptions,
  isElementObstructed,
  waitForElementUnblocked,
} from '../utils/walkthroughProviderDomHelpers'
import {
  detectOpenModal,
  ensurePortalInteractive,
  setPortalPassThrough,
  waitForModalClose,
} from '../utils/walkthroughProviderModalHelpers'
import {getComponentFromSteps} from '../utils/walkthroughRouteRegistry'
import {
  DynamicWalkthroughContext,
  WalkthroughActions,
  WalkthroughSessionMeta,
  WalkthroughStartOptions,
  WalkthroughState,
  WalkthroughStep,
  initialWalkthroughState,
} from './DynamicWalkthroughContext'
import {DynamicWalkthroughOverlay} from './DynamicWalkthroughOverlay'

let globalEventListenerActive = false
let activeInstanceId: string | null = null
const FORCE_CLEANUP_EVENT = 'dynamic-walkthrough-force-cleanup'

const PENDING_WALKTHROUGH_KEY = 'pending-walkthrough'

const MODAL_BLOCK_TIMEOUT = 1 * 60 * 1000

interface PendingWalkthrough {
  apiResponse: any
  timestamp: number
  currentStepIndex?: number
  sessionMeta?: WalkthroughSessionMeta
}

interface DynamicWalkthroughProviderProps {
  children: React.ReactNode
}

function DynamicWalkthroughProvider({children}: DynamicWalkthroughProviderProps) {
  const theme = getTheme()

  const [remountKey, setRemountKey] = useState(0)

  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const dispatch = useDispatch()

  const resetReduxSelections = useCallback(() => {
    dispatch({type: 'REDUX_SET_SELECTED_BRANCH_ID', payload: null})
    dispatch({type: 'REDUX_SET_SELECTED_LEVEL_ID', payload: null})
    dispatch({type: 'REDUX_SET_SELECTED_DURATION_ID', payload: null})
    dispatch({type: 'REDUX_SET_SELECTED_VERSION_ID', payload: null})
    ;[
      'fromTimetableView',
      'fromTimetableConstraints',
      'fromTeacherWorkload',
      'fromTimetableVisualization',
      'fromWorkloadBreakdown',
    ].forEach((key) => {
      sessionStorage.removeItem(key)
    })
    const tenant = resolveTimetableTenant(DEFAULT_TENANT_VALUE, TENANT_KEY)
    clearTimetableSelectionFromSession(tenant)
  }, [dispatch])
  const instanceId = useRef(
    `provider-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  ).current

  const [state, setState] = useState<WalkthroughState>(initialWalkthroughState)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [pendingSteps, setPendingSteps] = useState<WalkthroughStep[]>([])
  const [pendingApiResponse, setPendingApiResponse] = useState<any>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [, setCompletionToastVisible] = useState(false)
  const [, setCompletionToastMessage] = useState<string | null>(null)
  const [domCache, setDomCache] = useState<DOMCache | null>(null)
  const [isWaitingForModal, setIsWaitingForModal] = useState(false)
  const [completionStatus, setCompletionStatus] = useState<'success' | 'ended' | null>(null)
  const [sessionMeta, setSessionMeta] = useState<WalkthroughSessionMeta>({
    source: 'chatbot',
    openChatOnFinish: true,
  })

  const prevChatOpen = useRef<boolean>(false)
  const startingPathRef = useRef<string>('')
  const restorationAttempted = useRef<boolean>(false)
  const walkthroughInProgress = useRef<boolean>(false)
  const postNavigationInProgress = useRef<boolean>(false)
  const lastEventTimestamp = useRef<number>(0)
  const modalOpenedByWalkthrough = useRef<boolean>(false)
  const modalWatchInProgress = useRef<boolean>(false)
  const modalDetectThrottleRef = useRef<number>(0)
  const modalDetectCooldownUntilRef = useRef<number>(0)
  const modalWatchMountedRef = useRef<boolean>(true)
  const portalRootRef = useRef<HTMLElement | null>(null)

  const silentModeRef = useRef<boolean>(false)

  const completionMessage = useMemo(() => {
    if (completionStatus === 'success') {
      return intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.INFO.COMPLETED'})
    } else if (completionStatus === 'ended') {
      return intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.INFO.ENDED'})
    }
    return null
  }, [completionStatus, intl])

  const isStepPermitted = useCallback((step: WalkthroughStep): boolean => {
    const perm = step.walkthrough.permission
    if (!perm) return true
    if (Array.isArray(perm)) {
      return perm.some((p) => hasPermission(p))
    }
    return hasPermission(perm)
  }, [])

  const {cleanupPreviousStep, closeChatIfOpen, waitForUserAction} = React.useMemo(
    () =>
      createProviderDomHelpers({
        prevChatOpenRef: prevChatOpen,
        modalOpenedByWalkthroughRef: modalOpenedByWalkthrough,
        modalWatchInProgressRef: modalWatchInProgress,
        setIsWaitingForModal,
        setState,
        intl,
      }),
    [intl]
  )

  const checkPendingWalkthrough = useCallback((): PendingWalkthrough | null => {
    return Persistence.checkPendingWalkthrough()
  }, [])

  useEffect(() => {
    let root = document.getElementById('walkthrough-overlay-portal-root') as HTMLElement | null
    if (!root) {
      root = document.createElement('div')
      root.id = 'walkthrough-overlay-portal-root'
      document.body.appendChild(root)
    }
    portalRootRef.current = root

    return () => {
      try {
        if (portalRootRef.current && portalRootRef.current.parentElement) {
          portalRootRef.current.remove()
        }
      } catch (err) {
        calculateErrorMessage(err)
      } finally {
        portalRootRef.current = null
      }
    }
  }, [])

  const continueWalkthroughAfterNavigation = useCallback(
    async (apiResponse: any, resumeIndex: number = 0, options?: WalkthroughStartOptions) => {
      const resolvedMeta: WalkthroughSessionMeta = {
        source: options?.source ?? 'chatbot',
        openChatOnFinish: options?.openChatOnFinish ?? true,
      }
      setSessionMeta(resolvedMeta)
      const steps = parseApiResponse(apiResponse).map((s) => ({
        ...s,
        walkthrough: {
          ...s.walkthrough,
          tooltipMode: s.walkthrough.tooltipMode || inferTooltipMode(s),
        },
      }))
      if (steps.length === 0) return

      const safeSetCompletionToastMessage = (m: string) => {
        if (!silentModeRef.current) setCompletionToastMessage(m)
      }
      const safeSetCompletionToastVisible = (v: boolean) => {
        if (!silentModeRef.current) setCompletionToastVisible(v)
      }

      const ctx = {
        state,
        setState,
        domCache,
        setDomCache,
        sessionMeta: resolvedMeta,
        refs: {
          startingPathRef,
          restorationAttempted,
          walkthroughInProgress,
          postNavigationInProgress,
          lastEventTimestamp,
          modalOpenedByWalkthrough,
        },
        helpers: {
          closeChatIfOpen,
          findAndTagElement,
          scrollToElement,
          createSmartNavigator,
          buildDomCache,
          documentReadyHelper,
          getComponentFromSteps,
          cleanupPreviousStep,
          waitForUserAction,
          navigate: (p: string) => navigate(p),
          location,
          navigateFn: navigate,
          intl,
          completionMessage,
          setIsTransitioning,
          setCompletionToastMessage: safeSetCompletionToastMessage,
          setCompletionToastVisible: safeSetCompletionToastVisible,
          setPortalPassThrough,
        },
      }

      await StepActions.startWalkthrough(steps, apiResponse, ctx as any, resumeIndex)
    },
    [
      state,
      domCache,
      closeChatIfOpen,
      cleanupPreviousStep,
      waitForUserAction,
      location,
      navigate,
      intl,
      completionMessage,
    ]
  )

  const startWalkthrough = useCallback(
    async (
      steps: WalkthroughStep[],
      apiResponseOrOptions?: any,
      options?: WalkthroughStartOptions
    ) => {
      resetReduxSelections()
      setRemountKey((k) => k + 1)
      const looksLikeApiResponse = !!(
        apiResponseOrOptions &&
        (apiResponseOrOptions?.result?.walkthrough || apiResponseOrOptions?.walkthrough)
      )
      const resolvedOptions = looksLikeApiResponse ? options : apiResponseOrOptions
      const apiResponse = looksLikeApiResponse ? apiResponseOrOptions : undefined
      const resolvedMeta: WalkthroughSessionMeta = {
        source: resolvedOptions?.source ?? 'chatbot',
        openChatOnFinish: resolvedOptions?.openChatOnFinish ?? true,
      }
      setSessionMeta(resolvedMeta)
      const enriched = steps.map((s) => ({
        ...s,
        walkthrough: {
          ...s.walkthrough,
          tooltipMode: s.walkthrough.tooltipMode || inferTooltipMode(s),
        },
      }))

      const ctx = {
        state,
        setState,
        domCache,
        setDomCache,
        sessionMeta: resolvedMeta,
        refs: {
          startingPathRef,
          restorationAttempted,
          walkthroughInProgress,
          postNavigationInProgress,
          lastEventTimestamp,
          modalOpenedByWalkthrough,
        },
        helpers: {
          closeChatIfOpen,
          findAndTagElement,
          scrollToElement,
          createSmartNavigator,
          buildDomCache,
          documentReadyHelper,
          getComponentFromSteps,
          cleanupPreviousStep,
          waitForUserAction,
          navigate: (p: string) => navigate(p),
          location,
          navigateFn: navigate,
          intl,
          completionMessage,
          setIsTransitioning,
          setCompletionToastMessage,
          setCompletionToastVisible,
          setPortalPassThrough,
        },
      }

      await StepActions.startWalkthrough(enriched, apiResponse, ctx as any)
    },
    [
      state,
      domCache,
      closeChatIfOpen,
      cleanupPreviousStep,
      waitForUserAction,
      location,
      navigate,
      intl,
      completionMessage,
      resetReduxSelections,
    ]
  )

  const nextStep = useCallback(async () => {
    if (state.currentStepIndex >= state.steps.length - 1) {
      performFullWalkthroughCleanup({
        refs: {
          walkthroughInProgress,
          postNavigationInProgress,
          modalOpenedByWalkthrough,
        },
        helpers: {setPortalPassThrough, setIsTransitioning},
        setState,
        reason: 'provider nextStep reached last step',
      })

      if (startingPathRef.current && startingPathRef.current !== location.pathname) {
        navigate(startingPathRef.current)
      } else {
        window.scrollTo(0, 0)
      }
      startingPathRef.current = ''

      {
        const msg = intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.INFO.COMPLETED'})
        try {
          if (!silentModeRef.current) {
            setCompletionToastMessage(msg)
            setCompletionToastVisible(true)
            setTimeout(() => setCompletionToastVisible(false), 3500)
          }
        } catch (e) {
          calculateErrorMessage(e)
        }
        if (!silentModeRef.current) {
          window.dispatchEvent(
            new CustomEvent('dynamic-walkthrough-end', {
              detail: {completed: true, completionMsg: msg, sessionMeta},
            })
          )
        }
      }
      return
    }

    setIsTransitioning(true)
    cleanupPreviousStep()
    await new Promise((resolve) => setTimeout(resolve, 150))

    let nextIndex = state.currentStepIndex + 1
    let attempts = 0
    const maxAttempts = state.steps.length - nextIndex

    while (attempts < maxAttempts) {
      const nextStepData = state.steps[nextIndex]
      if (!isStepPermitted(nextStepData)) {
        nextIndex++
        attempts++
        continue
      }

      try {
        const elementResult = await findAndTagElement(
          nextStepData.walkthrough.target,
          nextStepData.id,
          getQuickFindOptions(domCache)
        )

        if (!elementResult.success || !elementResult.element) {
          nextIndex++
          attempts++

          if (nextIndex >= state.steps.length) {
            performFullWalkthroughCleanup({
              refs: {
                walkthroughInProgress,
                postNavigationInProgress,
                modalOpenedByWalkthrough,
              },
              helpers: {setPortalPassThrough, setIsTransitioning},
              setState,
              reason: 'provider nextStep loop no elements',
            })

            if (startingPathRef.current && startingPathRef.current !== location.pathname) {
              navigate(startingPathRef.current)
            } else {
              window.scrollTo(0, 0)
            }
            startingPathRef.current = ''

            {
              const msg = intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.INFO.ENDED'})
              try {
                if (!silentModeRef.current) {
                  setCompletionToastMessage(msg)
                  setCompletionToastVisible(true)
                  setTimeout(() => setCompletionToastVisible(false), 3500)
                }
              } catch (e) {
                calculateErrorMessage(e)
              }
              if (!silentModeRef.current) {
                window.dispatchEvent(
                  new CustomEvent('dynamic-walkthrough-end', {
                    detail: {completed: false, completionMsg: msg, sessionMeta},
                  })
                )
              }
            }
            return
          }

          continue
        }

        try {
          const newElementBlocked = isElementObstructed(elementResult.element)

          if (newElementBlocked) {
            setState((prev) => ({
              ...prev,
              error: intl.formatMessage({
                id: 'WALKTHROUGH_PROVIDER.ERROR.STILL_BLOCKED',
              }),
            }))
            setPortalPassThrough(true)

            const unblocked = await waitForElementUnblocked(
              elementResult.element,
              MODAL_BLOCK_TIMEOUT
            )

            setPortalPassThrough(false)
            if (!unblocked) {
              return
            }
            setState((prev) => ({...prev, error: null}))
          }
        } catch (err) {
          calculateErrorMessage(err)
        }

        setIsTransitioning(false)
        if (nextStepData.walkthrough.scroll !== 'none') {
          scrollToElement(elementResult.element)
        }

        setState((prev) => ({
          ...prev,
          currentStepIndex: nextIndex,
          currentElement: elementResult.element,
          isNavigating: false,
          error: null,
        }))
        Persistence.updatePendingWalkthroughProgress(nextIndex)

        break
      } catch (error) {
        calculateErrorMessage(error)
        nextIndex++
        attempts++

        if (nextIndex >= state.steps.length) {
          performFullWalkthroughCleanup({
            refs: {
              walkthroughInProgress,
              postNavigationInProgress,
              modalOpenedByWalkthrough,
            },
            helpers: {setPortalPassThrough, setIsTransitioning},
            setState,
            reason: 'provider nextStep catch',
          })

          if (startingPathRef.current && startingPathRef.current !== location.pathname) {
            navigate(startingPathRef.current)
          } else {
            window.scrollTo(0, 0)
          }
          startingPathRef.current = ''

          {
            const msg = intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.INFO.ENDED'})
            try {
              if (!silentModeRef.current) {
                setCompletionToastMessage(msg)
                setCompletionToastVisible(true)
                setTimeout(() => setCompletionToastVisible(false), 3500)
              }
            } catch (e) {
              calculateErrorMessage(e)
            }
            if (!silentModeRef.current) {
              window.dispatchEvent(
                new CustomEvent('dynamic-walkthrough-end', {
                  detail: {completed: false, completionMsg: msg, sessionMeta},
                })
              )
            }
          }
          return
        }
      }
    }

    if (attempts >= maxAttempts) {
      performFullWalkthroughCleanup({
        refs: {
          walkthroughInProgress,
          postNavigationInProgress,
          modalOpenedByWalkthrough,
        },
        helpers: {setPortalPassThrough, setIsTransitioning},
        setState,
        reason: 'provider nextStep attempts exhausted',
      })

      if (startingPathRef.current && startingPathRef.current !== location.pathname) {
        navigate(startingPathRef.current)
      } else {
        window.scrollTo(0, 0)
      }
      startingPathRef.current = ''
      const msg = intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.INFO.ENDED'})
      if (!silentModeRef.current) {
        window.dispatchEvent(
          new CustomEvent('dynamic-walkthrough-end', {
            detail: {completed: false, completionMsg: msg, sessionMeta},
          })
        )
      }
    }
  }, [
    state.currentStepIndex,
    state.steps,
    cleanupPreviousStep,
    location.pathname,
    navigate,
    intl,
    sessionMeta,
    isStepPermitted,
    domCache,
  ])

  useEffect(() => {
    if (!state.isActive) return

    modalWatchMountedRef.current = true
    const MODAL_WATCH_INTERVAL = 400
    const MODAL_WATCH_THROTTLE = 200

    const MODAL_REDETECT_COOLDOWN = 800

    const shouldHandleModal = () =>
      modalWatchMountedRef.current &&
      state.isActive &&
      !isWaitingForModal &&
      !modalWatchInProgress.current &&
      !modalOpenedByWalkthrough.current &&
      Date.now() > modalDetectCooldownUntilRef.current

    const handleModalDetected = async (modalElement: HTMLElement) => {
      if (!shouldHandleModal()) return

      modalWatchInProgress.current = true
      modalOpenedByWalkthrough.current = true
      setIsWaitingForModal(true)
      setPortalPassThrough(true)
      setState((prev) => ({
        ...prev,
        error: intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.PAUSED'}),
      }))

      try {
        const closed = await waitForModalClose(modalElement, MODAL_BLOCK_TIMEOUT)
        if (closed) {
          setState((prev) => ({...prev, error: null}))
          modalDetectCooldownUntilRef.current = Date.now() + MODAL_REDETECT_COOLDOWN
        } else {
          setState((prev) => ({
            ...prev,
            error: intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.MODAL_STILL_OPEN'}),
          }))
        }
      } finally {
        if (modalWatchMountedRef.current) {
          setIsWaitingForModal(false)
          setPortalPassThrough(false)
        }
        modalOpenedByWalkthrough.current = false
        modalWatchInProgress.current = false
      }

      if (!modalWatchMountedRef.current || !state.isActive) return
      const currentStep = state.steps[state.currentStepIndex]
      const action = currentStep?.walkthrough?.action
      const target = currentStep?.walkthrough?.target

      if (currentStep && action && action !== 'none' && target) {
        try {
          const actionSuccess = await waitForUserAction(target, action, currentStep.id)
          if (actionSuccess && state.isActive) {
            cleanupPreviousStep()
            await nextStep()
          }
        } catch (err) {
          calculateErrorMessage(err)
        }
      }
    }

    const maybeDetectModal = () => {
      if (!shouldHandleModal()) return
      const now = Date.now()
      if (now - modalDetectThrottleRef.current < MODAL_WATCH_THROTTLE) return
      modalDetectThrottleRef.current = now

      const modalElement = detectOpenModal()
      if (modalElement) {
        void handleModalDetected(modalElement)
      }
    }

    const interactionHandler = () => {
      maybeDetectModal()
    }

    window.addEventListener('click', interactionHandler, true)
    window.addEventListener('pointerup', interactionHandler, true)
    window.addEventListener('keydown', interactionHandler, true)

    const intervalId = window.setInterval(() => {
      maybeDetectModal()
    }, MODAL_WATCH_INTERVAL)

    return () => {
      modalWatchMountedRef.current = false
      window.removeEventListener('click', interactionHandler, true)
      window.removeEventListener('pointerup', interactionHandler, true)
      window.removeEventListener('keydown', interactionHandler, true)
      window.clearInterval(intervalId)
    }
  }, [
    state.isActive,
    state.currentStepIndex,
    state.steps,
    isWaitingForModal,
    intl,
    cleanupPreviousStep,
    nextStep,
    waitForUserAction,
  ])

  const previousStep = useCallback(async () => {
    if (state.currentStepIndex <= 0) {
      return
    }

    try {
      cleanupPreviousStep()
    } catch (e) {
      calculateErrorMessage(e)
    }

    const originalIndex = state.currentStepIndex
    let prevIndex = originalIndex - 1
    let attempts = 0
    const maxAttempts = originalIndex

    while (attempts < maxAttempts && prevIndex >= 0) {
      const prevStepData = state.steps[prevIndex]
      if (!isStepPermitted(prevStepData)) {
        prevIndex--
        attempts++
        setState((prev) => ({
          ...prev,
          currentStepIndex: Math.max(0, prevIndex),
        }))
        continue
      }
      try {
        const elementResult = await findAndTagElement(
          prevStepData.walkthrough.target,
          prevStepData.id,
          getQuickFindOptions(domCache)
        )

        if (!elementResult.success || !elementResult.element) {
          prevIndex--
          attempts++
          setState((prev) => ({
            ...prev,
            currentStepIndex: Math.max(0, prevIndex),
          }))
          continue
        }

        if (prevStepData.walkthrough.scroll !== 'none') {
          scrollToElement(elementResult.element)
        }

        setState((prev) => ({
          ...prev,
          currentElement: elementResult.element,
          isNavigating: false,
          error: null,
          currentStepIndex: prevIndex,
        }))
        Persistence.updatePendingWalkthroughProgress(prevIndex)

        return
      } catch (err) {
        prevIndex--
        attempts++
        setState((prev) => ({
          ...prev,
          currentStepIndex: Math.max(0, prevIndex),
        }))
        calculateErrorMessage(err)
      }
    }

    try {
      const originalStep = state.steps[originalIndex]
      const restoreResult = await findAndTagElement(
        originalStep.walkthrough.target,
        originalStep.id,
        getQuickFindOptions(domCache)
      )

      setState((prev) => ({
        ...prev,
        currentStepIndex: originalIndex,
        currentElement: restoreResult.success ? restoreResult.element : null,
        isNavigating: false,
        error: restoreResult.success
          ? null
          : intl.formatMessage(
              {
                id: 'WALKTHROUGH_PROVIDER.ERROR.COULD_NOT_FIND_PREVIOUS',
              },
              {target: originalStep.walkthrough.target}
            ),
      }))
      Persistence.updatePendingWalkthroughProgress(originalIndex)
    } catch (restoreErr) {
      setState((prev) => ({
        ...prev,
        currentStepIndex: originalIndex,
        currentElement: null,
        isNavigating: false,
        error: intl.formatMessage({
          id: 'WALKTHROUGH_PROVIDER.ERROR.COULD_NOT_FIND_PREVIOUS_GENERIC',
        }),
      }))
      Persistence.updatePendingWalkthroughProgress(originalIndex)
      calculateErrorMessage(restoreErr)
    }
  }, [state.currentStepIndex, state.steps, cleanupPreviousStep, domCache, intl, isStepPermitted])

  const goToStep = useCallback(
    async (index: number) => {
      if (index < 0 || index >= state.steps.length) {
        return
      }
      try {
        cleanupPreviousStep()
      } catch (e) {
        calculateErrorMessage(e)
      }

      const targetStep = state.steps[index]
      if (!isStepPermitted(targetStep)) {
        setState((prev) => ({
          ...prev,
          isNavigating: false,
          error: 'You do not have permission to access this step.',
        }))
        return
      }

      const executionMode = targetStep.execution ?? 'route'
      if (executionMode === 'local') {
        setState((prev) => ({
          ...prev,
          currentStepIndex: index,
          currentElement: null,
          isNavigating: false,
          error: null,
        }))
        Persistence.updatePendingWalkthroughProgress(index)
        return
      }

      try {
        const elementResult = await findAndTagElement(
          targetStep.walkthrough.target,
          targetStep.id,
          getQuickFindOptions(domCache)
        )

        if (!elementResult.success || !elementResult.element) {
          setState((prev) => ({
            ...prev,
            isNavigating: false,
            error: intl.formatMessage(
              {
                id: 'WALKTHROUGH_PROVIDER.ERROR.COULD_NOT_FIND_ELEMENT',
              },
              {target: targetStep.walkthrough.target}
            ),
          }))
          return
        }

        if (targetStep.walkthrough.scroll !== 'none') {
          scrollToElement(elementResult.element)
        }

        setState((prev) => ({
          ...prev,
          currentStepIndex: index,
          currentElement: elementResult.element,
          isNavigating: false,
          error: null,
        }))
        Persistence.updatePendingWalkthroughProgress(index)
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isNavigating: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }))
      }
    },
    [cleanupPreviousStep, domCache, intl, state.steps, isStepPermitted]
  )

  const endWalkthrough = useCallback(
    (aborted: boolean = false) => {
      const allStepsCompleted = state.currentStepIndex + 1 >= (state.steps?.length || 0)
      const isCompleted = !aborted && allStepsCompleted

      cleanupAllWalkthroughAttributes()
      sessionStorage.removeItem(PENDING_WALKTHROUGH_KEY)
      walkthroughInProgress.current = false
      modalOpenedByWalkthrough.current = false
      setState(initialWalkthroughState)

      try {
        ensurePortalInteractive()
      } catch (e) {
        calculateErrorMessage(e)
      }

      const portalRoot = document.getElementById('walkthrough-overlay-portal-root')
      if (portalRoot) {
        portalRoot.remove()
      }

      try {
        document
          .querySelectorAll(
            '.walkthrough-backdrop, .walkthrough-highlight, .walkthrough-tooltip, .walkthrough-loading-overlay, .walkthrough-transition-loader'
          )
          .forEach((el) => el.remove())
        const body = document.body
        if (body) body.style.pointerEvents = ''
      } catch (err) {
        calculateErrorMessage(err)
      }

      try {
        if (activeInstanceId === instanceId) {
          globalEventListenerActive = false
          activeInstanceId = null
        }
      } catch (err) {
        calculateErrorMessage(err)
      }

      try {
        window.dispatchEvent(new CustomEvent(FORCE_CLEANUP_EVENT))
      } catch (err) {
        calculateErrorMessage(err)
      }

      if (startingPathRef.current && startingPathRef.current !== location.pathname) {
        navigate(startingPathRef.current)
      } else {
        window.scrollTo(0, 0)
      }
      startingPathRef.current = ''

      const completionMsg = isCompleted
        ? intl.formatMessage({id: 'WALKTHROUGH_PROVIDER.SUCCESS_MESSAGE'})
        : intl.formatMessage({
            id: 'WALKTHROUGH_PROVIDER.INFO.ABORTED',
          })

      window.dispatchEvent(
        new CustomEvent('dynamic-walkthrough-end', {
          detail: {
            completed: isCompleted,
            completionMsg,
            sessionMeta,
          },
        })
      )
    },
    [
      state.currentStepIndex,
      state.steps?.length,
      location.pathname,
      intl,
      sessionMeta,
      instanceId,
      navigate,
    ]
  )

  const pauseWalkthrough = useCallback(() => {
    setState((prev) => ({...prev, isActive: false}))
  }, [])

  const resumeWalkthrough = useCallback(() => {
    setState((prev) => ({...prev, isActive: true}))
  }, [])

  const setCurrentElement = useCallback((element: HTMLElement | null) => {
    setState((prev) => ({...prev, currentElement: element}))
  }, [])

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({...prev, error}))
  }, [])

  const actions: WalkthroughActions & any = useMemo(
    () => ({
      startWalkthrough,
      nextStep,
      previousStep,
      goToStep,
      endWalkthrough,
      pauseWalkthrough,
      resumeWalkthrough,
      setCurrentElement,
      setError,
      setCompletionStatus: (status: 'success' | 'ended' | null) => {
        setCompletionStatus(status)
      },
      setSilentMode: (v: boolean) => {
        silentModeRef.current = !!v
      },
    }),
    [
      startWalkthrough,
      nextStep,
      previousStep,
      goToStep,
      endWalkthrough,
      pauseWalkthrough,
      resumeWalkthrough,
      setCurrentElement,
      setError,
    ]
  )

  const contextValue = useMemo(
    () => ({
      state: {...state, isTransitioning, isWaitingForModal},
      actions,
      sessionMeta,
    }),
    [state, isTransitioning, isWaitingForModal, actions, sessionMeta]
  )

  useEffect(() => {
    if (globalEventListenerActive) {
      return
    }

    globalEventListenerActive = true
    activeInstanceId = instanceId

    const handler = (e: Event) => {
      resetReduxSelections()

      try {
        const now = Date.now()
        if (now - lastEventTimestamp.current < 1000) {
          return
        }
        lastEventTimestamp.current = now

        const detail: any = (e as CustomEvent).detail
        if (!detail?.apiResponse) {
          return
        }

        const steps = parseApiResponse(detail.apiResponse)

        if (detail.confirmed) {
          startWalkthrough(steps, detail.apiResponse)
        } else {
          setPendingSteps(steps)
          setPendingApiResponse(detail.apiResponse)
          setShowConfirmation(true)
        }
      } catch (err) {
        calculateErrorMessage(err)
      }
    }

    window.addEventListener('dynamic-walkthrough-start', handler as EventListener)

    const forceHandler = () => {
      try {
        cleanupAllWalkthroughAttributes()
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        modalOpenedByWalkthrough.current = false
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        walkthroughInProgress.current = false
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        postNavigationInProgress.current = false
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        setState(initialWalkthroughState)
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        setIsTransitioning(false)
        setIsWaitingForModal(false)
      } catch (e) {
        calculateErrorMessage(e)
      }

      try {
        setPortalPassThrough(false)
      } catch (e) {
        calculateErrorMessage(e)
      }
    }

    window.addEventListener(FORCE_CLEANUP_EVENT, forceHandler as EventListener)

    return () => {
      if (activeInstanceId === instanceId) {
        globalEventListenerActive = false
        activeInstanceId = null
      }
      window.removeEventListener('dynamic-walkthrough-start', handler as EventListener)
      window.removeEventListener(FORCE_CLEANUP_EVENT, forceHandler as EventListener)
    }
  }, [instanceId, startWalkthrough, dispatch, resetReduxSelections])

  useEffect(() => {
    if (restorationAttempted.current) return

    const pending = checkPendingWalkthrough()
    if (pending) {
      restorationAttempted.current = true
      continueWalkthroughAfterNavigation(
        pending.apiResponse,
        pending.currentStepIndex ?? 0,
        pending.sessionMeta
      )
    }
  }, [location.pathname, checkPendingWalkthrough, continueWalkthroughAfterNavigation])

  useEffect(() => {
    if (state.isActive) return

    try {
      if (prevChatOpen.current) {
        const toggleBtn = document.getElementById('kt_drawer_chat_toggle')
        const drawer = document.getElementById('kt_drawer_chat')
        if (toggleBtn && typeof (toggleBtn as HTMLElement).click === 'function') {
          ;(toggleBtn as HTMLElement).click()
        } else if (drawer) {
          drawer.classList.add('show')
        }
      }
    } catch (err) {
      calculateErrorMessage(err)
    }
  }, [state.isActive])

  return (
    <DynamicWalkthroughContext.Provider value={contextValue}>
      {React.isValidElement(children) ? (
        React.cloneElement(children, {key: remountKey})
      ) : (
        <React.Fragment key={remountKey}>{children}</React.Fragment>
      )}

      {state.isActive &&
        createPortal(
          <DynamicWalkthroughOverlay theme={theme} />,
          portalRootRef.current || document.body
        )}

      <PromptModal
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false)
          setPendingSteps([])
          setPendingApiResponse(null)
        }}
        title={intl.formatMessage({
          id: 'WALKTHROUGH_PROVIDER.CONFIRM_TITLE',
        })}
        body={
          <p>
            {intl.formatMessage({
              id: 'WALKTHROUGH_PROVIDER.CONFIRM_BODY',
            })}
          </p>
        }
        primaryLabel={intl.formatMessage({
          id: 'WALKTHROUGH_PROVIDER.BUTTON.START',
        })}
        onPrimary={() => {
          setShowConfirmation(false)
          if (pendingSteps.length > 0 && pendingApiResponse) {
            startWalkthrough(pendingSteps, pendingApiResponse)
            setPendingSteps([])
            setPendingApiResponse(null)
          }
        }}
        secondaryLabel={intl.formatMessage({
          id: 'WALKTHROUGH_PROVIDER.BUTTON.CANCEL',
        })}
        onSecondary={() => {
          setShowConfirmation(false)
          setPendingSteps([])
          setPendingApiResponse(null)
        }}
        placement='center'
        width={520}
      />
    </DynamicWalkthroughContext.Provider>
  )
}

export default DynamicWalkthroughProvider
